import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import '@aytm/lib/rfd-ui/styles/advanced/color-complex.scss'
import useOuterClickListener from '@aytm/lib/rfd-ui/lib/outer-click-listener'
import { OverlayPanel } from 'primereact/overlaypanel'

const CLASS = 'rfd-ui-color-complex'

const palette = [
  { upper: { background: '#88d1cd', text: '#000000' }, lower: { background: '#c3e8e6', text: '#000000' } },
  { upper: { background: '#8db3d4', text: '#000000' }, lower: { background: '#c6d9ea', text: '#000000' } },
  { upper: { background: '#b9d376', text: '#000000' }, lower: { background: '#d6e5af', text: '#000000' } },
  { upper: { background: '#ffdd00', text: '#000000' }, lower: { background: '#8ab51a', text: '#000000' } },
  { upper: { background: '#ffdd00', text: '#000000' }, lower: { background: '#29abe2', text: '#000000' } },
  { upper: { background: null, text: '#000000' }, lower: { background: '#88d1cd', text: '#000000' } },
  { upper: { background: '#c3e8e6', text: '#000000' }, lower: { background: null, text: '#000000' } }
]

const onPanelClick = event => event.stopPropagation()

const copy = obj => {
  if (!obj) return obj
  const res = {}
  if ('string' === typeof obj) return obj
  else if ('object' === typeof obj) for (const [key, value] of Object.entries(obj)) res[key] = copy(value)
  return res
}

const ColorComplex = props => {
  const panelRef = useRef()
  const containerRef = useRef()
  const pickerRef = useRef()

  const lastColors = useRef(props.colors)

  const [show, setShow] = useState(false)
  const [custom, setCustom] = useState(false)

  const [colors, setColors] = useState(() => props.colors)

  useEffect(() => {
    if (JSON.stringify(props.colors) !== JSON.stringify(lastColors.current)) {
      lastColors.current = props.colors
      setColors(props.colors)
    }
  }, [props.colors])

  const onColorsClick = colors => () => {
    setShow(false)
    if (props.onSelect) {
      const newColors = copy(colors)
      props.onSelect({ name: props.name, colors: newColors })
    }
  }

  const onInputChange = type => event => {
    const inputColor = event.target.value
    const newColors = copy(colors)
    if ('upper.background' === type) newColors.upper.background = inputColor
    else if ('upper.text' === type) newColors.upper.text = inputColor
    else if ('lower.background' === type) newColors.lower.background = inputColor
    else if ('lower.text' === type) newColors.lower.text = inputColor
    setColors(newColors)
    if (CSS.supports('color', inputColor) && props.onSelect) {
      props.onSelect({ name: props.name, colors: newColors })
    }
  }

  const onInputKeyPress = event => {
    if (['escape', 'Enter'].includes(event.key)) {
      setShow(false)
    }
  }

  const onPickerClick = () => {
    if (!props.disabled) {
      if (show) {
        panelRef.current?.hide?.()
      }
      setTimeout(() => {
        setShow(!show)
        setTimeout(() => {
          if (!show) {
            panelRef.current?.show?.(null, pickerRef.current)
          }
        }, 10)
      }, 100)
    }
  }

  const onCustomClick = () => {
    setCustom(!custom)
  }

  const getClassName = (name, ...names) =>
    `${CLASS}-${name}${props.disabled ? ` ${CLASS}-${name}-disabled` : ''}${
      names.length ? names.map(v => ` ${CLASS}-${v}`) : ''
    }`

  const renderPicker = event => {
    const [upperBackground, lowerBackground] = [
      event.colors?.upper?.background || '#fff',
      event.colors?.lower?.background || '#fff'
    ]
    const [upperStyle, lowerStyle] = [
      upperBackground ? { backgroundColor: upperBackground } : null,
      lowerBackground ? { backgroundColor: lowerBackground } : null
    ]
    return (
      <div
        className={getClassName('picker', event.className)}
        onClick={event.onClick}
        key={event.key || 'picker'}
        ref={event.ref}
      >
        <div className={`${CLASS}-picker-upper`} style={upperStyle}></div>
        <div className={`${CLASS}-picker-lower`} style={lowerStyle}></div>
      </div>
    )
  }

  const renderPanel = () => {
    const containerRect = containerRef.current ? containerRef.current.getBoundingClientRect() : null
    const pickerRect = pickerRef.current ? pickerRef.current.getBoundingClientRect() : null
    const style =
      containerRect && pickerRect
        ? {
            // left: `${pickerRect.left - containerRect.left}px`,
            width: `${custom ? 2 * pickerRect.width : pickerRect.width}px`
            // top: `${containerRect.height + 4}px`
          }
        : {}
    const button = custom ? 'palette' : 'custom'
    let items = []
    if (custom) {
      items.push(
        <div key={1} className={`${CLASS}-caption`}>
          {'Background:'}
        </div>
      )
      items.push(
        <div key={2} className={`${CLASS}-line`}>
          <input
            className={`${CLASS}-input ${CLASS}-input-upper`}
            type={'text'}
            value={colors.upper.background || ''}
            onChange={onInputChange('upper.background')}
            onKeyDown={onInputKeyPress}
          />
          <input
            className={`${CLASS}-input  ${CLASS}-input-lower`}
            type={'text'}
            value={colors.lower.background || ''}
            onChange={onInputChange('lower.background')}
            onKeyDown={onInputKeyPress}
          />
        </div>
      )
      items.push(
        <div key={3} className={`${CLASS}-caption`}>
          {'Text:'}
        </div>
      )
      items.push(
        <div key={4} className={`${CLASS}-line`}>
          <input
            className={`${CLASS}-input ${CLASS}-input-upper`}
            type={'text'}
            value={colors.upper.text || ''}
            onChange={onInputChange('upper.text')}
            onKeyDown={onInputKeyPress}
          />
          <input
            className={`${CLASS}-input ${CLASS}-input-lower`}
            type={'text'}
            value={colors.lower.text || ''}
            onChange={onInputChange('lower.text')}
            onKeyDown={onInputKeyPress}
          />
        </div>
      )
    } else {
      items = palette.map((colors, index) =>
        renderPicker({ colors, key: index, className: 'item', onClick: onColorsClick(colors) })
      )
    }

    return (
      <OverlayPanel
        key={'panel'}
        className={`${CLASS}-panel-wrapper`}
        ref={panelRef}
        onClick={onPanelClick}
        prime-fixed-dropdown-dir="bottom"
        aytm-overlay-panel-patch="true"
      >
        <div className={`${CLASS}-panel`} style={style}>
          {items}
          <div className={`${CLASS}-divider`}></div>
          <div className={`${CLASS}-button`} onClick={onCustomClick}>
            {button}
          </div>
        </div>
      </OverlayPanel>
    )
    // return (
    //   <div key={'panel'} ref={panelRef} className={`${CLASS}-panel`} style={style} onClick={onPanelClick}>
    //     {items}
    //     <div className={`${CLASS}-divider`}></div>
    //     <div className={`${CLASS}-button`} onClick={onCustomClick}>
    //       {button}
    //     </div>
    //   </div>
    // )
  }

  const panel = show ? renderPanel() : null

  const label = props.label ? <div className={getClassName('label')}>{props.label}</div> : null

  const iconClass = show
    ? `icon-aytmfd-up ${CLASS}-icon ${CLASS}-icon-selector`
    : `icon-aytmfd-down ${CLASS}-icon ${CLASS}-icon-selector`

  const className = props.className ? `${CLASS} ${props.className}` : CLASS

  useOuterClickListener({ ref: panelRef, listen: show, onClick: () => setShow(false) })

  return (
    <div className={className} ref={containerRef}>
      {label}
      {renderPicker({ colors: props.colors, ref: pickerRef, onClick: onPickerClick })}
      <i className={iconClass} onClick={onPickerClick} />
      {panel}
    </div>
  )
}

ColorComplex.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  show: PropTypes.any,
  label: PropTypes.any,
  colors: PropTypes.object,
  disabled: PropTypes.any,
  onSelect: PropTypes.func,
  onClick: PropTypes.func
}

ColorComplex.palette = palette

export default ColorComplex
